import { useLocation } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { For, Show } from "solid-js";
import { ISSUE_PAGE_MATCH } from "../../enums/scan";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import { matchLabel, Score } from "./issueDetailParts";
import { currentPageEvidenceForGroup } from "./issuePageEvidence";
import type { CurrentIssuePageGroup } from "./issuePageResolution";
import { pageDetailHref } from "./pageDetailNavigation";
import { pageContentFitnessScore } from "./scanModel";

export function AffectedPagesTable(props: {
  data: OverviewResponse;
  websiteId: string;
  issueId: string;
  pageGroups: CurrentIssuePageGroup[];
}) {
  const location = useLocation();
  return (
    <section class="scan-panel">
      <div class="scan-section-head">
        <div>
          <span class="scan-kicker">Affected pages</span>
          <h2>Where this issue appears</h2>
        </div>
      </div>
      <Show
        when={props.pageGroups.length > 0}
        fallback={
          <p class="scan-muted">
            No affected page links resolve in the current scan.
          </p>
        }
      >
        <div class="scan-table-wrap">
          <table class="scan-table scan-affected-pages-table">
            <thead>
              <tr>
                <th>Page</th>
                <th>Content Fitness</th>
                <th>Agent diagnostic</th>
                <th>Supporting evidence</th>
              </tr>
            </thead>
            <tbody>
              <For each={props.pageGroups}>
                {(row) => (
                  <AffectedPageRow
                    data={props.data}
                    websiteId={props.websiteId}
                    issueId={props.issueId}
                    row={row}
                    locationSearch={location.search}
                  />
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </section>
  );
}

function AffectedPageRow(props: {
  data: OverviewResponse;
  websiteId: string;
  issueId: string;
  row: CurrentIssuePageGroup;
  locationSearch: string;
}) {
  const evidence = () => currentPageEvidenceForGroup(props.data, props.row);
  const readableCount = () => evidence().readable.length;
  const technicalCount = () => evidence().technical.length;
  const evidenceCount = () => readableCount() + technicalCount();
  const disclosureLabel = () =>
    readableCount() > 0
      ? `Page evidence (${readableCount()})`
      : `Technical evidence (${technicalCount()})`;
  const match = () =>
    props.row.resolutions.some(
      (resolution) => resolution.match !== ISSUE_PAGE_MATCH.ID,
    )
      ? ISSUE_PAGE_MATCH.PATH
      : ISSUE_PAGE_MATCH.ID;
  return (
    <tr>
      <td data-label="Page">
        <a
          class="scan-table-link"
          href={pageDetailHref(props.websiteId, props.row.page.id, {
            source: "issue",
            issueId: props.issueId,
            issueReturnQuery: props.locationSearch,
          })}
        >
          {pageDisplayLabel(props.row.page, props.data.site)}
        </a>
        <div class="scan-table-sub mono">{rawPagePath(props.row.page)}</div>
      </td>
      <td data-label="Content Fitness">
        <Score value={pageContentFitnessScore(props.row.page)} />
      </td>
      <td data-label="Agent diagnostic">
        <Score value={props.row.page.agentScore ?? null} />
      </td>
      <td data-label="Page evidence">
        <Show
          when={evidenceCount() > 0}
          fallback={
            <span class="scan-muted">No linked evidence is stored</span>
          }
        >
          <details class="scan-table-disclosure">
            <summary>{disclosureLabel()}</summary>
            <Show when={readableCount() > 0}>
              <ul class="scan-evidence-list">
                <For each={evidence().readable}>
                  {(item) => <li>{item.value}</li>}
                </For>
              </ul>
            </Show>
            <Show when={technicalCount() > 0}>
              <Show
                when={readableCount() > 0}
                fallback={
                  <TechnicalEvidence
                    items={evidence().technical}
                    match={match()}
                  />
                }
              >
                <details class="scan-technical-disclosure">
                  <summary>Technical details ({technicalCount()})</summary>
                  <div class="scan-disclosure-body">
                    <TechnicalEvidence
                      items={evidence().technical}
                      match={match()}
                    />
                  </div>
                </details>
              </Show>
            </Show>
          </details>
        </Show>
      </td>
    </tr>
  );
}

function TechnicalEvidence(props: {
  items: ReturnType<typeof currentPageEvidenceForGroup>["technical"];
  match: string;
}) {
  return (
    <>
      <dl>
        <dt>Page association</dt>
        <dd>
          <span class={`scan-match scan-match-${props.match}`}>
            {matchLabel(props.match)}
          </span>
        </dd>
      </dl>
      <ul class="scan-evidence-list mono">
        <For each={props.items}>{(item) => <li>{item.value}</li>}</For>
      </ul>
    </>
  );
}
